// Pure Cartellino (monthly timesheet and break computations) for Fide.
// Conforms to ADR 0008 (Recorded breaks and computable time).
//
// Runs identically in the web portal (React), mobile app (React Native),
// and backend Edge Functions / Node test runners.
// All calendar and local wall times are calculated in Europe/Rome.

import { isItalianWorkingDay } from './italy/calendar';
import { allocationEntries, type LeaveAllocation } from './leaveAllocation';
import type { EmploymentTerms } from './employment';
import { workCalendar, type CalendarEntry, calendarOverrides } from './workCalendar';
import type { WorkSchedule } from './workSchedule';

export const CARTELLINO_TZ = 'Europe/Rome';
export const LONG_SHIFT_MINUTES = 16 * 60; // 16 hours

export interface MealVoucherPolicy {
  company_id?: string;
  enabled: boolean;
  min_worked_minutes: number;
  include_smart_working: boolean;
  include_paid_breaks: boolean;
}

export function isMealVoucherDay(input: {
  workedMinutes: number;
  paidBreakMinutes: number;
  isSmartWorking?: boolean;
  policy?: MealVoucherPolicy | null;
}): boolean {
  const policy = input.policy;
  if (!policy || !policy.enabled) return false;
  const eligibleMinutes = policy.include_paid_breaks
    ? input.workedMinutes
    : Math.max(0, input.workedMinutes - input.paidBreakMinutes);
  if (eligibleMinutes >= policy.min_worked_minutes) return true;
  if (policy.include_smart_working && input.isSmartWorking) return true;
  return false;
}

export interface BreakPolicy {
  id: string;
  company_id: string;
  name: string;
  is_paid: boolean;
  is_mandatory: boolean;
  min_duration_minutes: number;
  expected_duration_minutes: number;
  valid_from: string; // YYYY-MM-DD
  valid_to: string | null;
}

export type TimesheetAnomaly =
  | 'missing_out'
  | 'missing_in'
  | 'unclosed_break'
  | 'break_without_start'
  | 'break_outside_shift'
  | 'short_break'
  | 'long_shift'
  | 'corrected'
  | 'flagged'
  | 'schedule_discrepancy';

export interface TimesheetBreak {
  start: string; // HH:MM
  end: string | null; // HH:MM
  durationMinutes: number;
  isPaid: boolean;
  policyId: string | null;
  policyName: string | null;
  anomalies: TimesheetAnomaly[];
}

export interface TimesheetDay {
  date: string; // YYYY-MM-DD
  dayOfWeek: number; // 1 = Monday, 7 = Sunday
  expectedMinutes: number | null;
  firstIn: string | null;
  lastOut: string | null;
  observedSpanMinutes: number;
  workedMinutes: number; // Net computable working minutes
  paidBreakMinutes: number;
  unpaidBreakMinutes: number;
  breaks: TimesheetBreak[];
  absences: string[];
  anomalies: TimesheetAnomaly[];
  isSmartWorking?: boolean;
  mealVoucherEarned?: boolean;
}

export interface TimesheetPunch {
  id?: string;
  member_id: string;
  punch_type: 'in' | 'out' | 'break_start' | 'break_end';
  method: 'geo' | 'qr' | 'manual';
  device_ts: string;
  flags: string[];
  break_policy_id?: string | null;
}

export interface TimesheetLeave {
  member_id: string;
  start_date: string;
  end_date: string;
  quantity: number;
  daily_allocation?: LeaveAllocation | null;
  leave_types: { code: string; unit: 'days' | 'hours' } | null;
}

export interface TimesheetMonthlySummary {
  month: string; // YYYY-MM
  daysWorked: number;
  expectedTotalMinutes: number;
  workedTotalMinutes: number;
  paidBreakTotalMinutes: number;
  unpaidBreakTotalMinutes: number;
  balanceMinutes: number;
  anomaliesCount: number;
  absences: Record<string, { days: number; hours: number }>;
  mealVouchersEarned?: number;
}

const romeTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: CARTELLINO_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function localRomeParts(iso: string): { date: string; time: string } {
  const p = Object.fromEntries(romeTimeFormatter.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

export function daysInMonth(month: string): string[] {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
    throw new Error(`invalid_month_format: ${month}`);
  }
  const [y, m] = month.split('-').map(Number);
  const totalDays = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return Array.from({ length: totalDays }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** Formats decimal hours or days (e.g. 7.5 -> "7,5") */
export const formatUnitQuantity = (n: number) => String(Number(n)).replace('.', ',');

export function parseDayOfWeek(dateStr: string): number {
  const d = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** Pure wall-time timesheet computation for a single employee in a given month. */
export function buildCartellino(input: {
  month: string;
  memberId: string;
  punches: TimesheetPunch[];
  policies: BreakPolicy[];
  schedules?: WorkSchedule[];
  terms?: EmploymentTerms[];
  leaves?: TimesheetLeave[];
  calendarOverrides?: CalendarEntry[];
  mealVoucherPolicy?: MealVoucherPolicy | null;
}): { days: TimesheetDay[]; summary: TimesheetMonthlySummary } {
  const allDays = daysInMonth(input.month);
  const daySet = new Set(allDays);

  // 1. Calculate expected minutes from calendar/schedules if provided
  const expectedByDate = new Map<string, number | null>();
  if (input.schedules && input.terms && input.schedules.length > 0) {
    {
      const overrides = input.calendarOverrides ? calendarOverrides(input.calendarOverrides) : [];
      const nextDay=new Date(Date.parse(`${allDays.at(-1)}T00:00:00Z`)+86400000).toISOString().slice(0,10);
      const calDays = workCalendar(allDays[0]!,nextDay,input.schedules,overrides,input.terms);
      for (const cd of calDays) {
        if(daySet.has(cd.date)) expectedByDate.set(cd.date,cd.incomplete?null:0);
      }
      for(const cd of calDays)for(const interval of cd.intervals){
        if(daySet.has(interval.anchorDate)&&expectedByDate.get(interval.anchorDate)!==null)
          expectedByDate.set(interval.anchorDate,(expectedByDate.get(interval.anchorDate)??0)+interval.end-interval.start);
      }
    }
  }

  // 2. Index policies by id
  const policyMap = new Map<string, BreakPolicy>();
  for (const pol of input.policies) {
    policyMap.set(pol.id, pol);
  }

  // 3. Filter and sort member punches
  const memberPunches = input.punches
    .filter((p) => p.member_id === input.memberId)
    .sort((a, b) => a.device_ts.localeCompare(b.device_ts));

  // 4. Group punches by Rome local date (anchoring overnight shifts to start date)
  const punchesByDate = new Map<string, TimesheetPunch[]>();
  let shiftDate: string | null = null;
  for (const p of memberPunches) {
    const { date } = localRomeParts(p.device_ts);
    if (p.punch_type === 'in') shiftDate = date;
    const anchor = shiftDate ?? date;
    const group = punchesByDate.get(anchor) ?? [];
    group.push(p);
    punchesByDate.set(anchor, group);
    if (p.punch_type === 'out') shiftDate = null;
  }

  // 5. Allocate leave requests for this member
  const memberLeaves = (input.leaves ?? []).filter((l) => l.member_id === input.memberId);
  const absencesByDate = new Map<string, string[]>();
  const leaveSummary: Record<string, { days: number; hours: number }> = {};

  for (const r of memberLeaves) {
    if (!r.leave_types) continue;
    const { code, unit } = r.leave_types;
    const entries = allocateTimesheetLeave(r);

    for (const entry of entries) {
      if (daySet.has(entry.date)) {
        const list = absencesByDate.get(entry.date) ?? [];
        const label = unit === 'hours' ? `${code} ${formatUnitQuantity(entry.quantity)}h` : entry.quantity === 1 ? code : `${code} ${formatUnitQuantity(entry.quantity)}g`;
        list.push(label);
        absencesByDate.set(entry.date, list);

        const sum = (leaveSummary[code] ??= { days: 0, hours: 0 });
        if (unit === 'hours') sum.hours += entry.quantity;
        else sum.days += entry.quantity;
      }
    }
  }

  // 6. Build daily cartellino rows
  const days: TimesheetDay[] = [];

  for (const date of allDays) {
    const dayPunches = punchesByDate.get(date) ?? [];
    const expectedMinutes = expectedByDate.get(date) ?? null;
    const absences = absencesByDate.get(date) ?? [];
    const dayAnomalies: TimesheetAnomaly[] = [];

    const markDay = (a: TimesheetAnomaly) => {
      if (!dayAnomalies.includes(a)) dayAnomalies.push(a);
    };

    let firstIn: string | null = null;
    let lastOut: string | null = null;
    let minFirstInTs: number | null = null;
    let maxLastOutTs: number | null = null;

    let shiftOpenTs: number | null = null;
    let currentBreakStartTs: number | null = null;
    let currentBreakPunch: TimesheetPunch | null = null;

    let workedMs = 0;
    let paidBreakMinutes = 0;
    let unpaidBreakMinutes = 0;
    const breaks: TimesheetBreak[] = [];

    for (const p of dayPunches) {
      const ts = Date.parse(p.device_ts);
      const timeStr = localRomeParts(p.device_ts).time;

      if (p.method === 'manual') markDay('corrected');
      if (p.flags && p.flags.length > 0) markDay('flagged');

      switch (p.punch_type) {
        case 'in': {
          if (firstIn === null) {
            firstIn = timeStr;
            minFirstInTs = ts;
          }
          if (shiftOpenTs !== null) {
            // Consecutive 'in' without 'out'
            markDay('missing_out');
          }
          shiftOpenTs = ts;
          break;
        }
        case 'break_start': {
          if (shiftOpenTs === null) {
            markDay('break_outside_shift');
          }
          if (currentBreakStartTs !== null) {
            markDay('unclosed_break');
          }
          currentBreakStartTs = ts;
          currentBreakPunch = p;
          break;
        }
        case 'break_end': {
          if (currentBreakStartTs === null) {
            markDay('break_without_start');
            breaks.push({
              start: '—',
              end: timeStr,
              durationMinutes: 0,
              isPaid: false,
              policyId: p.break_policy_id ?? null,
              policyName: p.break_policy_id ? (policyMap.get(p.break_policy_id)?.name ?? null) : null,
              anomalies: ['break_without_start'],
            });
          } else {
            const durMin = Math.max(0, Math.round((ts - currentBreakStartTs) / 60_000));
            const policy = currentBreakPunch?.break_policy_id ? policyMap.get(currentBreakPunch.break_policy_id) : null;
            const isPaid = policy?.is_paid ?? false;
            const breakAnomalies: TimesheetAnomaly[] = [];

            if (policy && policy.min_duration_minutes > 0 && durMin < policy.min_duration_minutes) {
              breakAnomalies.push('short_break');
              markDay('short_break');
            }

            if (isPaid) {
              paidBreakMinutes += durMin;
            } else {
              unpaidBreakMinutes += durMin;
            }

            breaks.push({
              start: localRomeParts(currentBreakPunch!.device_ts).time,
              end: timeStr,
              durationMinutes: durMin,
              isPaid,
              policyId: policy?.id ?? null,
              policyName: policy?.name ?? null,
              anomalies: breakAnomalies,
            });

            currentBreakStartTs = null;
            currentBreakPunch = null;
          }
          break;
        }
        case 'out': {
          lastOut = timeStr;
          maxLastOutTs = ts;

          if (currentBreakStartTs !== null) {
            markDay('unclosed_break');
            currentBreakStartTs = null;
            currentBreakPunch = null;
          }

          if (shiftOpenTs !== null) {
            const shiftMs = Math.max(0, ts - shiftOpenTs);
            workedMs += shiftMs;
            if (shiftMs / 60_000 > LONG_SHIFT_MINUTES) {
              markDay('long_shift');
            }
            shiftOpenTs = null;
          } else {
            markDay('missing_in');
          }
          break;
        }
      }
    }

    if (shiftOpenTs !== null) {
      markDay('missing_out');
    }
    if (currentBreakStartTs !== null) {
      markDay('unclosed_break');
    }

    const observedSpanMinutes =
      minFirstInTs !== null && maxLastOutTs !== null ? Math.max(0, Math.round((maxLastOutTs - minFirstInTs) / 60_000)) : 0;

    // Total gross worked minus unpaid breaks + paid breaks = computable worked minutes
    const grossShiftMinutes = Math.round(workedMs / 60_000);
    // Unpaid breaks are excluded from gross shift time
    const netWorkedMinutes = Math.max(0, grossShiftMinutes - unpaidBreakMinutes);

    // Schedule discrepancy check
    if (expectedMinutes !== null && expectedMinutes > 0 && netWorkedMinutes === 0 && absences.length === 0) {
      markDay('schedule_discrepancy');
    }

    days.push({
      date,
      dayOfWeek: parseDayOfWeek(date),
      expectedMinutes,
      firstIn,
      lastOut,
      observedSpanMinutes,
      workedMinutes: netWorkedMinutes,
      paidBreakMinutes,
      unpaidBreakMinutes,
      breaks,
      absences,
      anomalies: dayAnomalies,
      isSmartWorking: absences.some((a) => a.startsWith('SMART_WORKING')),
      mealVoucherEarned: isMealVoucherDay({
        workedMinutes: netWorkedMinutes,
        paidBreakMinutes,
        isSmartWorking: absences.some((a) => a.startsWith('SMART_WORKING')),
        policy: input.mealVoucherPolicy,
      }),
    });
  }

  // 7. Monthly summary
  const daysWorked = days.filter((d) => d.workedMinutes > 0).length;
  const expectedTotalMinutes = days.reduce((sum, d) => sum + (d.expectedMinutes ?? 0), 0);
  const workedTotalMinutes = days.reduce((sum, d) => sum + d.workedMinutes, 0);
  const paidBreakTotalMinutes = days.reduce((sum, d) => sum + d.paidBreakMinutes, 0);
  const unpaidBreakTotalMinutes = days.reduce((sum, d) => sum + d.unpaidBreakMinutes, 0);
  const balanceMinutes = workedTotalMinutes - expectedTotalMinutes;
  const anomaliesCount = days.reduce((sum, d) => sum + d.anomalies.length, 0);

  const summary: TimesheetMonthlySummary = {
    month: input.month,
    daysWorked,
    expectedTotalMinutes,
    workedTotalMinutes,
    paidBreakTotalMinutes,
    unpaidBreakTotalMinutes,
    balanceMinutes,
    anomaliesCount,
    absences: leaveSummary,
    mealVouchersEarned: days.filter((d) => d.mealVoucherEarned).length,
  };

  return { days, summary };
}

/** Approved quantities are authoritative; ambiguous history requires reconciliation. */
export function allocateTimesheetLeave(r: TimesheetLeave): Array<{date:string;quantity:number}> {
 if (!r.leave_types) throw new Error('report_leave_allocation_required');
 const quantity=Number(r.quantity), unit=r.leave_types.unit;
 if(r.daily_allocation){
  if(r.daily_allocation.code!==r.leave_types.code)throw new Error('report_leave_allocation_required');
  return allocationEntries(r.daily_allocation,r.start_date,r.end_date,quantity,unit);
 }
 const from=Date.parse(`${r.start_date}T00:00:00Z`), to=Date.parse(`${r.end_date}T00:00:00Z`);
 if(!Number.isFinite(quantity)||quantity<=0||!Number.isFinite(from)||!Number.isFinite(to)||from>to||new Date(from).toISOString().slice(0,10)!==r.start_date||new Date(to).toISOString().slice(0,10)!==r.end_date)throw new Error('report_leave_allocation_required');
 if(from===to)return [{date:r.start_date,quantity}];
 if(unit!=='days')throw new Error('report_leave_allocation_required');
 const entries:Array<{date:string;quantity:number}>=[];
 for(let ms=from;ms<=to;ms+=86400000){const date=new Date(ms).toISOString().slice(0,10);if(r.leave_types.code==='MALATTIA'||isItalianWorkingDay(date))entries.push({date,quantity:1});}
 if(entries.length!==quantity)throw new Error('report_leave_allocation_required');
 return entries;
}
